import { z } from 'zod';
import { bootSearch } from '../../config/boot/bootUrl';

// Two coercion shapes preserve the existing per-param truthy semantics:
//
//   * onByOne: ON when value === '1' (most flags)
//   * offByZero: ON by default, OFF when value === '0'
//     (quickPicker / pickCharacter / simHud — UI flags that ship enabled)
//
// Adopting URLParams is a drop-in replacement for inline
// `params.get('foo') === '1'` / `params.get('foo') !== '0'` checks.

const onByOne = z
  .string()
  .optional()
  .transform((v) => v === '1');

const offByZero = z
  .string()
  .optional()
  .transform((v) => v !== '0'); // default true; '0' turns OFF

export const URLParamsSchema = z.object({
  // Routing / launch shape
  preset: z.string().optional(),
  map: z.string().optional(),
  class: z.string().optional(),

  // Twitch OAuth round-trip status (set by server redirect)
  twitch: z.string().optional(),

  // Boot-time flags ('1' = on)
  reset: onByOne,
  skipSplash: onByOne,
  skipLogin: onByOne,
  vqa: onByOne,

  // UI flags ('0' = off, default true)
  pickCharacter: offByZero,
  quickPicker: offByZero,
  simHud: offByZero,

  // Diagnostics / debug ('1' = on)
  debug: onByOne,
  debugSpellText: onByOne,
  diag: onByOne,
  diagAll: onByOne,
  labels: onByOne,
  /** Teleporter ids, destination plates, hitboxes, and link lines. */
  teleporterDebug: onByOne,
  followLeader: onByOne,
  simAutoStart: onByOne,

  // Future expansions (declared so consumers can opt in once written)
  mute: onByOne,
  wsUrl: z.string().url().optional(),
});

export type URLParams = z.infer<typeof URLParamsSchema>;

/**
 * Parse the current URL search string into a typed `URLParams` object.
 *
 * - Defaults to the page's boot query (bootUrl.ts — survives the main-domain
 *   address-bar clean) when called from the browser.
 * - Returns full defaults (every flag off / undefined) when invoked outside
 *   the browser (SSR, jest jsdom without location).
 * - NEVER throws: a malformed search string falls back to defaults.
 *
 * Mirrors DJ-Engine settings rule #6 (no half-loaded states): a bad URL
 * never bricks the client.
 */
export function parseURLParams(search?: string): URLParams {
  const raw = pickSearchString(search);
  const obj: Record<string, string> = {};
  try {
    const sp = new URLSearchParams(raw);
    sp.forEach((value, key) => {
      obj[key] = value;
    });
  } catch {
    // malformed — fall through with empty obj
  }
  const result = URLParamsSchema.safeParse(obj);
  return result.success ? result.data : URLParamsSchema.parse({});
}

/**
 * Teleporter authoring overlays are intentionally opt-in. Normal gameplay
 * keeps the authored portal glow and overlap prompt without exposing map ids,
 * destination plates, hitboxes, or connection lines.
 */
export function isTeleporterDebugEnabled(search?: string): boolean {
  return parseURLParams(search).teleporterDebug;
}

function pickSearchString(search: string | undefined): string {
  if (typeof search === 'string') return search;
  return bootSearch();
}
